import React, { useEffect, useRef, useState } from "react";
import crashImage from "assets/puzzles/iserba/roadSafety/crash.svg";
import successImage from "assets/puzzles/iserba/roadSafety/success.svg";
import fondTransition from "assets/puzzles/iserba/roadSafety/interieur_voiture_recuperation_collegue.svg";
import fondTransition2 from "assets/puzzles/iserba/roadSafety/interieur_voiture.svg";
import deuxiemePersonnage from "assets/puzzles/iserba/roadSafety/deuxieme_personnage.svg";
import TypewriterText from "components/shared/TypeWriterText";

const Game1ValidationScreen = ({ isPuzzleCorrect, onRetry, onNext }) => {
	const gridRef = useRef(null);
	const [gridSize, setGridSize] = useState(0);
	const [showCrashDetails, setShowCrashDetails] = useState(false);
	const [transitionToNext, setTransitionToNext] = useState(false);
	const [isRetryEnabled, setIsRetryEnabled] = useState(false);
	const [isContinueEnabled, setIsContinueEnabled] = useState(false);
	const [backgroundImage, setBackgroundImage] = useState("none");
	const [driverText, setDriverText] = useState(null);
	const [passengerText, setPassengerText] = useState(null);
	const [narratorText, setNarratorText] = useState(null);

	// Determine success
	const success =
		isPuzzleCorrect.scene_1_correct && isPuzzleCorrect.scene_2_correct;

	useEffect(() => {
		const updateSize = () => {
			if (gridRef.current) {
				const size = Math.min(window.innerWidth, window.innerHeight - 300);
				setGridSize(size);
			}
		};
		updateSize();
		window.addEventListener("resize", updateSize);
		return () => window.removeEventListener("resize", updateSize);
	}, []);

	useEffect(() => {
		if (!success) {
			setBackgroundImage(`url(${crashImage})`);
			// After 2 seconds, show crash details and enable retry button
			setTimeout(() => {
				setBackgroundImage(`none`);
				setShowCrashDetails(true);
				setIsRetryEnabled(true);
			}, 2000);
		} else {
			setBackgroundImage(`url(${successImage})`);
			setIsContinueEnabled(true);
		}
	}, [success]);

	const handleClickNext = () => {
		setTransitionToNext(true);
		setBackgroundImage(`url(${fondTransition})`);

		setNarratorText({
			text: "Vous récupérez votre collègue...",
			onClickFinish: () => {
				setBackgroundImage(`url(${fondTransition2})`);
				setNarratorText({
					text: "...et vous vous mettez en route pour votre prochain rendez-vous.",
					onClickFinish: () => {
						setNarratorText(null);
						setDriverText({
							text: "— Tiens, et si on écoutait la radio ?",
							onClickFinish: () => {
								setDriverText(null);
								setPassengerText({
									text: "— Bonne idée !",
									onClickFinish: () => {
										onNext();
									},
								});
							},
						});
					},
				});
			},
		});
	};

	const getCrashText = () => {
		let _crashCause = "";
		if (isPuzzleCorrect.scene_1_errors.length > 0) {
			_crashCause = isPuzzleCorrect.scene_1_errors[0];
		} else if (isPuzzleCorrect.scene_2_errors.length > 0) {
			_crashCause = isPuzzleCorrect.scene_2_errors[0];
		}

		if (_crashCause === "essuie-glace") {
			return "Si seulement votre essuie-glace avait fonctionné ! \n\n Cela aurait évité à plusieurs voitures d'être accidentées.";
		} else if (_crashCause === "retroviseur") {
			return "Une vis de votre rétroviseur s'est détachée, et vous n'aviez plus de visibilité. \n\n Vous n'avez pas vu le cycliste sur votre gauche et vous l'avez heurté.";
		} else if (_crashCause === "roue") {
			return "Difficile de garder le contrôle d'un véhicule, avec un pneu sous-gonflé. \n\n Ce n'est pourtant pas dans le poteau que vous vouliez aller !";
		} else if (_crashCause === "barre-proteinée") {
			return "Vous êtes sûr·e de ne pas pouvoir attendre pour manger ? \n\n En regardant la route, vous auriez remarqué le fossé.";
		} else if (_crashCause === "connection-bluetooth") {
			return "Vous avez reçu un appel et regardé votre téléphone au lieu de la route. \n\n  Vous avez coupé la route au bus, qui n'a pas eu le temps de freiner. Votre portière passager a subi le choc.";
		} else if (_crashCause === "appel-client") {
			return "Vous rouliez trop vite, de peur d'être en retard. \n\n Pourtant, la vitesse excessive et les distances trop courtes entre véhicules sont très dangereuses.";
		} else if (_crashCause === "sac") {
			return "La route est plus importante que le sac. \n\n Vous auriez dû tourner… et vous avez fini dans un mur !";
		} else if (_crashCause === "calibration-gps") {
			return "Vous avez oublié de calibrer votre GPS. \n\n En le programmant en roulant, vous n’avez remarqué ni le feu rouge, ni le piéton qui traversait.";
		}
		return "Oops ! Un accident s'est produit. Essayez encore.";
	};

	return (
		<div className="flex flex-col items-start">
			<div
				ref={gridRef}
				className="relative aspect-square border-black bg-gray-400 border-8 shadow-[5px_5px_0px_rgba(0,0,0,1)]"
				style={{
					width: gridSize,
					height: gridSize,
					backgroundImage: backgroundImage,
					backgroundSize: transitionToNext ? "100%" : "60%",
					backgroundRepeat: "no-repeat",
					backgroundPosition: "center",
					backgroundColor: showCrashDetails ? "black" : "white",
					color: showCrashDetails ? "white" : "black",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					textAlign: "center",
					fontSize: "1.2rem",
					padding: "20px",
				}}
			>
				{transitionToNext && backgroundImage === `url(${fondTransition2})` && (
					<img
						src={deuxiemePersonnage}
						alt="deuxiemePersonnage"
						className="absolute bottom-0 right-0 w-full"
					/>
				)}
				{narratorText && (
					<div className="absolute top-[5%] left-8 right-8 text-sm py-2 px-4 shadow-md mb-4 text-center bg-white">
						<TypewriterText
							text={narratorText.text}
							onClickFinish={narratorText.onClickFinish}
						/>
					</div>
				)}
				{driverText && (
					<div className="absolute bottom-[75%] left-8 text-sm py-2 px-4 shadow-md mb-4 bg-white rounded-full">
						<TypewriterText
							text={driverText.text}
							onClickFinish={driverText.onClickFinish}
						/>
					</div>
				)}
				{passengerText && (
					<h3 className="absolute bottom-[60%] right-24 text-sm py-2 px-4 shadow-md mb-4 bg-white rounded-full">
						<TypewriterText
							text={passengerText.text}
							onClickFinish={passengerText.onClickFinish}
						/>
					</h3>
				)}
				{success && !transitionToNext && (
					<div className="absolute bottom-4 right-4 left-4 py-4 px-6 bg-white text-sm font-bold">
						<p>
							Bravo ! En vous préparant correctement, vous avez évité de
							nombreux accidents.
						</p>
					</div>
				)}
				{showCrashDetails && (
					<div
						className="flex justify-center items-center p-4 "
						style={{ width: gridSize + "px", height: "100%" }}
					>
						<p className="whitespace-pre-wrap break-words text-center text-white">
							<TypewriterText text={getCrashText()} />
						</p>
					</div>
				)}
			</div>

			<div className="mt-4 h-24 px-4 mb-8 flex w-full items-center justify-end">
				{!success && (
					<button
						onClick={onRetry}
						className="mt-2 rounded-full px-4 py-2 border-black text-white bg-[#fbb554] shadow-[6px_6px_0px_rgba(0,0,0,0.7)] hover:bg-[#ffa256] disabled:bg-slate-300"
						disabled={!isRetryEnabled}
					>
						Réessayer
					</button>
				)}
				{success && !transitionToNext && (
					<button
						onClick={handleClickNext}
						className="mt-2 rounded-full px-4 py-2 border-black text-white bg-[#fbb554] shadow-[6px_6px_0px_rgba(0,0,0,0.7)] hover:bg-[#ffa256] disabled:bg-slate-300"
						disabled={!isContinueEnabled}
					>
						Continuer
					</button>
				)}
			</div>
		</div>
	);
};

export default Game1ValidationScreen;
